import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../store";
import { isAdmin, isCompanyOwner, isCompanySpecialist } from "../../utils/permissions";
import { useTranslation } from "react-i18next";
import companiesService, { Company } from "../../features/companies/companiesService";
import { useDispatch } from "react-redux";
import { setSelectedClient } from "../../features/settings/settingsSlice";
import BrandGlyph from "../../components/common/BrandGlyph";


const AIToolsPage: React.FC = () => {
    const dispatch = useDispatch();
    const { user } = useAppSelector((s) => s.auth);
    const canView = isAdmin(user) || isCompanyOwner(user) || isCompanySpecialist(user);
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [query, setQuery] = useState("");
    const [singleCompanies, setSingleCompanies] = useState<Company[]>([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState<number | "">("");

    // Load single-type companies for Admin / Super Admin
    useEffect(() => {
        const load = async () => {
            if (!isAdmin(user)) return;
            const { companies } = await companiesService.getCompanies({ company_type: "single", limit: 200 });
            setSingleCompanies(companies || []);
        };
        load();
    }, [user]);

    const tools = useMemo(
        () => [
            {
                key: "ai-chatbots",
                title: t("ai_chatbots"),
                description: t("manage_your_ai_powered_chatbot_assistants"),
                icon: "bi-robot",
                to: "/ai-chatbots",
                colorFrom: "#3b82f6",
                colorTo: "#8b5cf6",
                category: "Chatbots",
                badges: ["Popular"],
                featured: true,
            },
            {
                key: "deep-research",
                title: t("deep_research"),
                description: t("deep_research_subtitle"),
                icon: "bi-search",
                to: "/ai-tools/deep-research",
                colorFrom: "#10b981",
                colorTo: "#14b8a6",
                category: "Research",
                badges: ["COMING SOON"],
            },
            {
                key: "whatsapp-chatbots",
                title: t("whatsapp_chatbots"),
                description: t("whatsapp_chatbots_subtitle"),
                icon: "bi-whatsapp",
                to: "/ai-tools/whatsapp-chatbots",
                colorFrom: "#22c55e",
                colorTo: "#16a34a",
                category: "Messaging",
                badges: ["COMING SOON"],
            },
        ],
        [t]
    );

    const categories = useMemo(() => {
        const set = new Set<string>(["All"]);
        tools.forEach((tool: any) => tool.category && set.add(tool.category));
        return Array.from(set);
    }, [tools]);

    const [activeCategory, setActiveCategory] = useState<string>("All");

    if (!canView) {
        return (
            <div className="ai-tools-page">
                <div className="brand-header">
                    <div className="brand-icon">
                        <BrandGlyph size={48} animated={true} />
                    </div>
                    <div className="brand-text">
                        <h1>{t("ai_tools")}</h1>
                        <p>{t("are_you_sure")}</p>
                    </div>
                </div>
            </div>
        );
    }

    const filtered = tools.filter((tool: any) => {
        const matchQuery =
            tool.title.toLowerCase().includes(query.toLowerCase()) ||
            tool.description.toLowerCase().includes(query.toLowerCase());
        const matchCategory = activeCategory === "All" || tool.category === activeCategory;
        return matchQuery && matchCategory;
    });

    return (
        <div className="ai-tools-page">
            <div className="page-header slide-in-down">
                <div className="header-content">
                    <div className="header-left">
                        <div className="header-icon-wrapper">
                            <i className="bi bi-stars"></i>
                        </div>
                        <div className="header-text">
                            <h1>{t("ai_tools")}</h1>
                            <p>{t("ai_tools_subtitle")}</p>
                        </div>
                    </div>
                    <div className="header-right">
                        <div className="filter-group">
                            <div className="search-box">
                                <i className="bi bi-search search-icon" />
                                <input
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder={t("tools_search_placeholder") as string}
                                    aria-label={t("tools_search_placeholder") as string}
                                />
                                {query && (
                                    <button
                                        type="button"
                                        className="clear-btn"
                                        onClick={() => setQuery("")}
                                    >
                                        <i className="bi bi-x-circle-fill" />
                                    </button>
                                )}
                            </div>

                            {isAdmin(user) && (
                                <div className="select-wrapper">
                                    <i className="bi bi-building select-icon"></i>
                                    <select
                                        value={selectedCompanyId}
                                        onChange={(e) => {
                                            const val = e.target.value ? Number(e.target.value) : "";
                                            setSelectedCompanyId(val);
                                            (dispatch as any)(setSelectedClient(typeof val === 'number' ? val : undefined));
                                        }}
                                    >
                                        <option value="">{t("select_company") || "Select company"}</option>
                                        {singleCompanies.map((c) => (
                                            <option key={c.id} value={c.id}>{c.company_name}</option>
                                        ))}
                                    </select>
                                    <i className="bi bi-chevron-down select-arrow"></i>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="content-container">
                {/* Category chips */}
                <div className="chip-container slide-in-up animate-delay-1">
                    <div className="chip-scroll">
                        {categories.map((cat) => (
                            <button
                                key={cat}
                                type="button"
                                className={`chip ${activeCategory === cat ? 'active' : ''}`}
                                onClick={() => setActiveCategory(cat)}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="tools-grid slide-in-up animate-delay-2">
                    {filtered.map((tool: any) => (
                        <div
                            key={tool.key}
                            className={`tool-card ${tool.featured ? 'featured' : ''}`}
                            onClick={() => navigate(tool.to)}
                            role="button"
                            tabIndex={0}
                        >
                            <div className="card-glow" style={{ background: `radial-gradient(circle at 50% 0%, ${tool.colorFrom}20, transparent 70%)` }}></div>

                            <div className="card-header">
                                <div
                                    className="tool-icon"
                                    style={{ background: `linear-gradient(135deg, ${tool.colorFrom}, ${tool.colorTo})` }}
                                >
                                    <i className={`bi ${tool.icon}`} />
                                </div>
                                {!!tool.badges?.length && (
                                    <div className="tool-badges">
                                        {tool.badges.map((b: string) => (
                                            <span className="badge" key={b} style={{ color: tool.colorTo, background: `${tool.colorTo}15` }}>{b}</span>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="card-body">
                                <h3 className="tool-title">{tool.title}</h3>
                                <p className="tool-desc">{tool.description}</p>
                            </div>

                            <div className="card-footer">
                                <span className="try-btn">
                                    Try Tool <i className="bi bi-arrow-right" />
                                </span>
                            </div>
                        </div>
                    ))}
                </div>

                {filtered.length === 0 && (
                    <div className="empty-state">
                        <div className="empty-icon-wrapper">
                            <BrandGlyph size={64} animated={false} />
                        </div>
                        <h3 className="empty-title">{t('no_results') || 'No tools found'}</h3>
                        <p className="empty-desc">{t('try_different_filters') || 'Try adjusting your search or filters to find what you looking for.'}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AIToolsPage;


